// Limits *per issue* within a *session/treatment scope* (from URL params or event tags).

export const MAX_EVENTS_PER_WINDOW = 3;
export const WINDOW_MS = 10 * 60 * 1000; // 10 minutes

// Prevent localStorage from growing without bounds for a given scope.
export const MAX_TRACKED_ISSUES_PER_SCOPE = 50;

// Drop old issue counters after this TTL (even if the same scopeKey is reused later).
export const ISSUE_STATE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// How many frames to include in the issue signature when fingerprint isn't provided.
export const TOP_FRAMES_TO_INCLUDE = 3;

// Prefix for localStorage keys.
export const STORAGE_KEY_PREFIX = 'sentry_message';

// If you have multiple frontends on the SAME origin (e.g., "client" and "practitioner"),
// set this differently per app to avoid cross-app throttling.
// - If you WANT shared throttling across apps, keep the same value in both.
// - If you want separate throttling, set 'client' vs 'practitioner'.
export const APP_NAMESPACE = 'shared';

// Cleanup runs at most once per interval (per browser profile).
export const CLEANUP_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

type EventFrame = {
  filename?: string;
  lineno?: number;
  colno?: number;
  function?: string;
  in_app?: boolean;
};

type EventExceptionValue = {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: EventFrame[];
  };
};

type EventLike = {
  // Sentry events can have `type: "transaction"` too, depending on hooks used.
  // We'll avoid throttling non-error events by default.
  type?: string;

  message?: string;
  fingerprint?: string[];
  exception?: {
    values?: EventExceptionValue[];
  };
  tags?: Record<string, unknown>;
};

type SessionScope = {
  sessionUUID: string | null;
  treatmentUUID: string | null;
};

// Stored per issueHash in localStorage (per scope key)
type StoredIssue = {
  // count within the current window
  c: number;
  // window reset timestamp (ms epoch)
  r: number;
  // last seen timestamp (ms epoch) - for pruning + cleanup
  ls: number;
};

type StoredIssueMap = Record<string, StoredIssue>;

// Cache parsed objects per storageKey to avoid JSON.parse per event storm.
// Important: if we can't persist, we disable limiter for that scope (fail open).
const scopeCache = new Map<string, StoredIssueMap>();
const disabledScopes = new Set<string>();

const HASH_MULTIPLIER = 31;
const HASH_MODULUS = 2147483647;

const CLEANUP_TS_KEY = `${STORAGE_KEY_PREFIX}:__cleanup_ts`;

// -------------------- helpers --------------------

const safeParse = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
};

const safeGetItem = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (storage: Storage, key: string, value: string): boolean => {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeRemoveItem = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
};

const hashString = (value: string): string => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * HASH_MULTIPLIER + value.charCodeAt(i)) % HASH_MODULUS;
  }
  return hash.toString(36);
};

const normalizeIssueText = (value: string): string =>
  value
    // ISO timestamps (e.g. 2026-02-11T02:33:07.249Z)
    .replace(/\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z\b/g, '<timestamp>')
    // UUIDs
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, '<uuid>')
    // Twilio connect counter noise
    .replace(/\bconnect\s+#\d+\b/gi, 'connect #<n>');

const normalizeOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

// -------------------- scope --------------------

const getSessionScopeFromLocation = (): SessionScope => {
  if (typeof window === 'undefined') {
    return { sessionUUID: null, treatmentUUID: null };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    sessionUUID: params.get('sessionUUID') ?? params.get('sessionId'),
    treatmentUUID: params.get('treatmentUUID') ?? params.get('treatmentId'),
  };
};

const getSessionScope = (event?: EventLike): SessionScope => {
  const tagSession =
    normalizeOptionalString(event?.tags?.sessionUUID) ?? normalizeOptionalString(event?.tags?.sessionId);
  const tagTreatment =
    normalizeOptionalString(event?.tags?.treatmentUUID) ?? normalizeOptionalString(event?.tags?.treatmentId);

  const locScope = getSessionScopeFromLocation();

  return {
    sessionUUID: tagSession ?? normalizeOptionalString(locScope.sessionUUID),
    treatmentUUID: tagTreatment ?? normalizeOptionalString(locScope.treatmentUUID),
  };
};

const getSessionScopeKey = (event?: EventLike): string | null => {
  const { sessionUUID, treatmentUUID } = getSessionScope(event);
  if (!sessionUUID && !treatmentUUID) return null;

  // Uses both IDs to keep counters exact per treatment session.
  return `session:${sessionUUID ?? 'none'}|treatment:${treatmentUUID ?? 'none'}`;
};

const getStorageKey = (event?: EventLike): string | null => {
  const scopeKey = getSessionScopeKey(event);
  if (!scopeKey) return null;
  return `${STORAGE_KEY_PREFIX}:${APP_NAMESPACE}:${scopeKey}`;
};

// issue key

const selectSignatureFrames = (frames: EventFrame[]): EventFrame[] => {
  if (!frames.length) return [];
  const inApp = frames.filter(f => f.in_app);
  const source = inApp.length ? inApp : frames;
  return source.slice(-TOP_FRAMES_TO_INCLUDE);
};

const frameToWhere = (f: EventFrame): string => {
  const file = f.filename ?? '';
  const line = typeof f.lineno === 'number' ? String(f.lineno) : '';
  const col = typeof f.colno === 'number' ? String(f.colno) : '';
  const fn = f.function ? `#${f.function}` : '';
  return `${file}:${line}:${col}${fn}`;
};

const getIssueKey = (event: EventLike): string => {
  // If you use custom fingerprinting in Sentry, align limiter with it.
  if (Array.isArray(event.fingerprint) && event.fingerprint.length) {
    return event.fingerprint.join('|');
  }

  const exception = event.exception?.values?.[0];
  const type = exception?.type ?? 'Error';
  const value = normalizeIssueText(exception?.value ?? event.message ?? 'unknown');

  const frames = exception?.stacktrace?.frames ?? [];
  const signatureFrames = selectSignatureFrames(frames);
  const where = signatureFrames.map(frameToWhere).join('|');

  return `${type}|${value}|${where}`;
};

//  storage map validation/pruning

const isValidStoredIssue = (v: unknown): v is StoredIssue => {
  if (!v || typeof v !== 'object') return false;
  const obj = v as StoredIssue;
  return typeof obj.c === 'number' && typeof obj.r === 'number' && typeof obj.ls === 'number';
};

const pruneIssues = (issues: StoredIssueMap, nowMs: number): boolean => {
  let changed = false;

  // Remove invalid or stale entries
  for (const [k, v] of Object.entries(issues)) {
    if (!isValidStoredIssue(v)) {
      delete issues[k];
      changed = true;
      continue;
    }
    if (nowMs - v.ls > ISSUE_STATE_TTL_MS) {
      delete issues[k];
      changed = true;
    }
  }

  // Cap number of tracked issues per scope (keep most recent)
  const keys = Object.keys(issues);
  if (keys.length > MAX_TRACKED_ISSUES_PER_SCOPE) {
    keys.sort((a, b) => (issues[b]?.ls ?? 0) - (issues[a]?.ls ?? 0));
    for (const k of keys.slice(MAX_TRACKED_ISSUES_PER_SCOPE)) {
      delete issues[k];
      changed = true;
    }
  }

  return changed;
};

const clearScopeCache = (storageKey: string): void => {
  scopeCache.delete(storageKey);
  disabledScopes.delete(storageKey);
};

const maybeRunGlobalCleanup = (storage: Storage, nowMs: number): void => {
  const lastCleanupMs = Number(safeGetItem(storage, CLEANUP_TS_KEY) ?? '0');
  if (Number.isFinite(lastCleanupMs) && nowMs - lastCleanupMs < CLEANUP_INTERVAL_MS) {
    return;
  }

  const scopePrefix = `${STORAGE_KEY_PREFIX}:${APP_NAMESPACE}:`;
  const scopeKeys: string[] = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(scopePrefix)) {
        scopeKeys.push(key);
      }
    }
  } catch {
    safeSetItem(storage, CLEANUP_TS_KEY, String(nowMs));
    return;
  }

  for (const storageKey of scopeKeys) {
    const raw = safeGetItem(storage, storageKey);
    const parsed = safeParse<StoredIssueMap>(raw);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      safeRemoveItem(storage, storageKey);
      clearScopeCache(storageKey);
      continue;
    }

    const issues = parsed as StoredIssueMap;
    const changed = pruneIssues(issues, nowMs);

    if (Object.keys(issues).length === 0) {
      safeRemoveItem(storage, storageKey);
      clearScopeCache(storageKey);
      continue;
    }

    scopeCache.set(storageKey, issues);
    if (changed) {
      persistIssuesForScope(storage, storageKey, issues);
    }
  }

  safeSetItem(storage, CLEANUP_TS_KEY, String(nowMs));
};

const readIssuesForScope = (storage: Storage, storageKey: string): StoredIssueMap => {
  if (scopeCache.has(storageKey)) return scopeCache.get(storageKey)!;

  const raw = safeGetItem(storage, storageKey);
  const parsed = safeParse<StoredIssueMap>(raw);

  const issues: StoredIssueMap = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  scopeCache.set(storageKey, issues);
  return issues;
};

const persistIssuesForScope = (storage: Storage, storageKey: string, issues: StoredIssueMap): boolean => {
  const ok = safeSetItem(storage, storageKey, JSON.stringify(issues));
  if (!ok) {
    // Fail open: if we can't persist state, disable limiter for this scope in this tab.
    disabledScopes.add(storageKey);
    scopeCache.delete(storageKey);
  }
  return ok;
};

// -------------------- main API --------------------

/**
 * Returns true if the event should be dropped (rate-limited), false otherwise.
 * Use inside Sentry `beforeSend`.
 */
export const shouldDropSentryEvent = (event: EventLike): boolean => {
  // Defensive: don't rate-limit transactions or non-error events via this hook.
  // (If you want to throttle transactions, do it separately via beforeSendTransaction.)
  if (event?.type && event.type !== 'error') return false;

  const storage = getStorage();
  if (!storage) return false;
  const nowMs = Date.now();
  maybeRunGlobalCleanup(storage, nowMs);

  const storageKey = getStorageKey(event);
  if (!storageKey) return false;

  // If localStorage is not writable (quota/private mode/etc), fail open.
  if (disabledScopes.has(storageKey)) return false;

  const issues = readIssuesForScope(storage, storageKey);

  const rawIssueKey = getIssueKey(event);
  const issueHash = hashString(rawIssueKey);

  let entry = issues[issueHash];
  let changed = false;

  if (!entry || !isValidStoredIssue(entry)) {
    entry = { c: 0, r: nowMs + WINDOW_MS, ls: nowMs };
    issues[issueHash] = entry;
    changed = true;
  }

  // Reset the window if needed
  if (nowMs >= entry.r) {
    entry.c = 0;
    entry.r = nowMs + WINDOW_MS;
    changed = true;
  }

  // If already at/over limit, drop WITHOUT writing to localStorage.
  // This avoids storage churn during an error storm.
  if (entry.c >= MAX_EVENTS_PER_WINDOW) {
    return true;
  }

  // Allow and increment
  entry.c += 1;
  entry.ls = nowMs;
  changed = true;

  // Prune (only matters when we persist)
  if (pruneIssues(issues, nowMs)) changed = true;

  // Persist updates; if it fails, fail open for this event
  if (changed) {
    const ok = persistIssuesForScope(storage, storageKey, issues);
    if (!ok) return false;
  }

  return false;
};

// Not required for correctness; useful for QA/reset button.

export const clearSentryIssueCountsForCurrentSession = (): void => {
  const storage = getStorage();
  if (!storage) return;

  const storageKey = getStorageKey(); // uses URL scope
  if (!storageKey) return;

  safeRemoveItem(storage, storageKey);
  scopeCache.delete(storageKey);
  disabledScopes.delete(storageKey);
};

export const runSentryIssueLimiterCleanup = (): void => {
  const storage = getStorage();
  if (!storage) return;

  maybeRunGlobalCleanup(storage, Date.now());
};
